import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { submitDetailedContact } from "../mock";
import { FileText, Upload, Calculator } from "lucide-react";

const TechnicalQuoteForm = () => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Informations client
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    
    // Détails du projet
    projectType: "",
    equipmentType: "",
    liftingCapacity: "",
    workingEnvironment: "",
    
    // Spécifications techniques
    spanLength: "",
    liftingHeight: "",
    railLength: "",
    powerSupply: "",
    frequency: "",
    workingDuty: "",
    
    // Conditions d'installation
    buildingType: "",
    floorConditions: "",
    ceilingHeight: "",
    accessRestrictions: "",
    
    // Exigences spéciales
    safetyRequirements: [],
    environmentalFactors: [],
    certificationNeeded: [],
    
    // Planning
    projectTimeline: "",
    installationDeadline: "",
    budget: "",
    
    // Description détaillée
    projectDescription: "",
    additionalRequirements: ""
  });

  const safetyOptions = [
    "Conformité CSA",
    "Conformité ASME", 
    "Zone ATEX",
    "Environnement alimentaire",
    "Protection anti-collision",
    "Système de surveillance"
  ];

  const environmentalOptions = [
    "Température extrême",
    "Humidité élevée",
    "Environnement corrosif",
    "Poussières industrielles",
    "Vibrations",
    "Espaces confinés"
  ];

  const certificationOptions = [
    "Inspection initiale",
    "Certification annuelle",
    "Formation opérateurs",
    "Documentation technique",
    "Plan de maintenance"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const technicalData = {
        ...formData,
        equipmentType: "Demande technique détaillée",
        message: `
PROJET: ${formData.projectType}
ÉQUIPEMENT: ${formData.equipmentType}

SPÉCIFICATIONS TECHNIQUES:
- Capacité de levage: ${formData.liftingCapacity}
- Portée: ${formData.spanLength}
- Hauteur de levage: ${formData.liftingHeight}
- Longueur de rail: ${formData.railLength}
- Alimentation: ${formData.powerSupply}
- Fréquence: ${formData.frequency}
- Classe de service: ${formData.workingDuty}

ENVIRONNEMENT:
- Type de bâtiment: ${formData.buildingType}
- Conditions de sol: ${formData.floorConditions}
- Hauteur de plafond: ${formData.ceilingHeight}
- Restrictions d'accès: ${formData.accessRestrictions}

EXIGENCES:
- Sécurité: ${formData.safetyRequirements.join(', ')}
- Environnement: ${formData.environmentalFactors.join(', ')}
- Certifications: ${formData.certificationNeeded.join(', ')}

PLANNING:
- Échéance: ${formData.projectTimeline}
- Installation: ${formData.installationDeadline}
- Budget estimé: ${formData.budget}

DESCRIPTION:
${formData.projectDescription}

EXIGENCES ADDITIONNELLES:
${formData.additionalRequirements}
        `
      };

      const result = await submitDetailedContact(technicalData, language);
      if (result.success) {
        toast.success(result.message);
        // Reset form
        setFormData({
          companyName: "", contactName: "", email: "", phone: "",
          projectType: "", equipmentType: "", liftingCapacity: "", workingEnvironment: "",
          spanLength: "", liftingHeight: "", railLength: "", powerSupply: "", frequency: "", workingDuty: "",
          buildingType: "", floorConditions: "", ceilingHeight: "", accessRestrictions: "",
          safetyRequirements: [], environmentalFactors: [], certificationNeeded: [],
          projectTimeline: "", installationDeadline: "", budget: "",
          projectDescription: "", additionalRequirements: ""
        });
      } else {
        toast.error(result.message || t("error"));
      }
    } catch (error) {
      console.error("Technical quote form error:", error);
      const errorMessage = language === "fr" 
        ? "Une erreur est survenue. Veuillez réessayer."
        : "An error occurred. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="bg-excel-steel text-white">
        <CardTitle className="text-2xl flex items-center">
          <Calculator className="h-6 w-6 mr-2" />
          Demande de devis technique détaillée
        </CardTitle>
      </CardHeader>
      <CardContent className="p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Informations client */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Informations client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Nom de l'entreprise *</Label>
                <Input
                  id="companyName"
                  required
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactName">Personne contact *</Label>
                <Input
                  id="contactName"
                  required
                  value={formData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Détails du projet */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Détails du projet
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="projectType">Type de projet *</Label>
                <Select onValueChange={(value) => handleInputChange('projectType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nouveau">Nouvelle installation</SelectItem>
                    <SelectItem value="remplacement">Remplacement d'équipement</SelectItem>
                    <SelectItem value="modernisation">Modernisation</SelectItem>
                    <SelectItem value="extension">Extension d'installation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="equipmentType">Type d'équipement *</Label>
                <Select onValueChange={(value) => handleInputChange('equipmentType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez l'équipement" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pont-roulant">Pont roulant</SelectItem>
                    <SelectItem value="grue-portique">Grue portique</SelectItem>
                    <SelectItem value="potence">Potence</SelectItem>
                    <SelectItem value="palan">Palan électrique</SelectItem>
                    <SelectItem value="manipulateur">Manipulateur industriel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Spécifications techniques */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Spécifications techniques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="liftingCapacity">Capacité de levage *</Label>
                <Select onValueChange={(value) => handleInputChange('liftingCapacity', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Capacité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5t">0.5 tonne</SelectItem>
                    <SelectItem value="1t">1 tonne</SelectItem>
                    <SelectItem value="2t">2 tonnes</SelectItem>
                    <SelectItem value="3t">3 tonnes</SelectItem>
                    <SelectItem value="5t">5 tonnes</SelectItem>
                    <SelectItem value="10t">10 tonnes</SelectItem>
                    <SelectItem value="20t">20 tonnes</SelectItem>
                    <SelectItem value="40t">40+ tonnes</SelectItem>
                    <SelectItem value="custom">Sur mesure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="spanLength">Portée (m)</Label>
                <Input
                  id="spanLength"
                  placeholder="ex: 12.5"
                  value={formData.spanLength}
                  onChange={(e) => handleInputChange('spanLength', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="liftingHeight">Hauteur de levage (m)</Label>
                <Input
                  id="liftingHeight"
                  placeholder="ex: 8.0"
                  value={formData.liftingHeight}
                  onChange={(e) => handleInputChange('liftingHeight', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="railLength">Longueur de rail (m)</Label>
                <Input
                  id="railLength"
                  placeholder="ex: 30.0"
                  value={formData.railLength}
                  onChange={(e) => handleInputChange('railLength', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="powerSupply">Alimentation électrique</Label>
                <Select onValueChange={(value) => handleInputChange('powerSupply', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tension" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="230V">230V monophasé</SelectItem>
                    <SelectItem value="400V">400V triphasé</SelectItem>
                    <SelectItem value="600V">600V triphasé</SelectItem>
                    <SelectItem value="custom">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="workingDuty">Classe de service</Label>
                <Select onValueChange={(value) => handleInputChange('workingDuty', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Classe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A1">A1 - Usage occasionnel</SelectItem>
                    <SelectItem value="A3">A3 - Usage modéré</SelectItem>
                    <SelectItem value="A5">A5 - Usage intensif</SelectItem>
                    <SelectItem value="A7">A7 - Usage très intensif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Conditions d'installation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Conditions d'installation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="buildingType">Type de bâtiment</Label>
                <Select onValueChange={(value) => handleInputChange('buildingType', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Type de structure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="acier">Structure acier</SelectItem>
                    <SelectItem value="beton">Structure béton</SelectItem>
                    <SelectItem value="mixte">Structure mixte</SelectItem>
                    <SelectItem value="bois">Structure bois</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ceilingHeight">Hauteur sous plafond (m)</Label>
                <Input
                  id="ceilingHeight"
                  placeholder="ex: 10.0"
                  value={formData.ceilingHeight}
                  onChange={(e) => handleInputChange('ceilingHeight', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accessRestrictions">Restrictions d'accès</Label>
              <Textarea
                id="accessRestrictions"
                placeholder="Décrivez les contraintes d'accès pour l'installation..."
                value={formData.accessRestrictions}
                onChange={(e) => handleInputChange('accessRestrictions', e.target.value)}
              />
            </div>
          </div>

          {/* Exigences spéciales */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Exigences spéciales
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">Exigences de sécurité</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {safetyOptions.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={`safety-${option}`}
                        checked={formData.safetyRequirements.includes(option)}
                        onCheckedChange={(checked) => handleCheckboxChange('safetyRequirements', option, checked)}
                      />
                      <Label htmlFor={`safety-${option}`} className="text-sm">{option}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-base font-medium">Facteurs environnementaux</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {environmentalOptions.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={`env-${option}`}
                        checked={formData.environmentalFactors.includes(option)}
                        onCheckedChange={(checked) => handleCheckboxChange('environmentalFactors', option, checked)}
                      />
                      <Label htmlFor={`env-${option}`} className="text-sm">{option}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Planning et budget */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Planning et budget
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="projectTimeline">Échéance du projet</Label>
                <Select onValueChange={(value) => handleInputChange('projectTimeline', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Délai souhaité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent (moins de 1 mois)</SelectItem>
                    <SelectItem value="1-3mois">1-3 mois</SelectItem>
                    <SelectItem value="3-6mois">3-6 mois</SelectItem>
                    <SelectItem value="6mois+">6+ mois</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget">Budget estimé</Label>
                <Select onValueChange={(value) => handleInputChange('budget', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Fourchette budgétaire" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="<25k">Moins de 25 000$</SelectItem>
                    <SelectItem value="25-50k">25 000$ - 50 000$</SelectItem>
                    <SelectItem value="50-100k">50 000$ - 100 000$</SelectItem>
                    <SelectItem value="100k+">100 000$+</SelectItem>
                    <SelectItem value="tbd">À déterminer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Description du projet */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Description détaillée
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="projectDescription">Description du projet *</Label>
                <Textarea
                  id="projectDescription"
                  rows={4}
                  required
                  placeholder="Décrivez votre projet en détail: objectifs, contraintes, contexte d'utilisation..."
                  value={formData.projectDescription}
                  onChange={(e) => handleInputChange('projectDescription', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="additionalRequirements">Exigences additionnelles</Label>
                <Textarea
                  id="additionalRequirements"
                  rows={3}
                  placeholder="Mentions spéciales, formations requises, documentation..."
                  value={formData.additionalRequirements}
                  onChange={(e) => handleInputChange('additionalRequirements', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Upload de fichiers */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-excel-dark border-b pb-2">
              Documents techniques
            </h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">
                Glissez vos fichiers ici ou cliquez pour parcourir
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Plans, photos, spécifications techniques (PDF, JPG, PNG, DWG)
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6">
            <Button 
              type="submit"
              disabled={isSubmitting}
              size="lg"
              className="w-full bg-excel-red hover:bg-excel-dark text-white"
            >
              {isSubmitting ? (
                "Envoi en cours..."
              ) : (
                <>
                  <FileText className="mr-2 h-5 w-5" />
                  Envoyer la demande de devis technique
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default TechnicalQuoteForm;